import { useTranslation } from "react-i18next";
import { Group, Text } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import { IconChevronLeft } from "@tabler/icons-react";
import { useShallow } from "zustand/react/shallow";

import { WithSettingsNavbar } from "@layouts/WithSettingsNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { PAGE_TITLE, ROLES } from "@constants/index";

import { useAppStore } from "@hooks/store/useAppStore";

import { StyledHeader } from "@styles/components/Header.styled";

import { AnalysisTab } from "./components/AnalysisTab";
import { ConsultationTab } from "./components/ConsultationTab";
import { ConsumablesTab } from "./components/ConsumablesTab";
import { TreatmentPlanTab } from "./components/TreatmentPlanTab";
import { PrimarySpecializationsTab } from "./components/PrimarySpecializationsTab";


const resourceManagementTabs = [
  "consultations",
  "analysis",
  "treatment-plans",
  "consumables",
  "primary-specializations",
];

const ResourceManagementContent = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("resource-management")} • ${PAGE_TITLE}`);

  // Use useAppStore directly for tab state
  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );

  const activeTab = resourceManagementTabs.includes(
    storeTabs.resourceManagementTab ?? ""
  )
    ? storeTabs.resourceManagementTab
    : "consultations";
  const activeSubTab = storeTabs.resourceManagementSubTab || "analysis";

  // Computed state
  const isCollectionMethodsActive = activeTab === 'analysis' && activeSubTab === 'collection-methods';

  // Navigation helpers
  const backToAnalysis = () => {
    setStoreTabs({ resourceManagementSubTab: 'analysis' });
  };

  return (
    <WithSidebar hasDashboardNavbar>
      <WithSettingsNavbar userRole={ROLES.ADMIN}>
        <TabsRoot
          value={activeTab as string}
          onValueChange={(val) => {
            if (val === 'analysis') {
              // When switching to analysis tab, ensure we're on the main analysis view
              setStoreTabs({
                resourceManagementTab: val,
                resourceManagementSubTab: 'analysis'
              });
            } else {
              // For other tabs, just set the main tab (no subtabs needed)
              setStoreTabs({
                resourceManagementTab: val
              });
            }
          }}
        >
          {isCollectionMethodsActive && (
            <div style={{ marginTop: '24px', paddingTop: '8px' }}>
              <Group gap={8} align="center">
                <IconChevronLeft size={16} color="#6B7280" />
                <Text
                  fz={14}
                  c="dimmed"
                  style={{ cursor: 'pointer' }}
                  onClick={backToAnalysis}>
                  {t("back-to-laboratory")}
                </Text>
              </Group>
            </div>
          )}

          <StyledHeader>
            {!isCollectionMethodsActive && (
              <TabsList
                comboboxItems={[
                  { label: t("consultations"), value: "consultations" },
                  { label: t("laboratory"), value: "analysis" },
                  { label: t("treatment-plans"), value: "treatment-plans" },
                  { label: t("consumables"), value: "consumables" },
                  { label: "Primary specializations", value: "primary-specializations" },
                ]}
              />
            )}
          </StyledHeader>

          <TabsContent value="consultations">
            <ConsultationTab />
          </TabsContent>
          <TabsContent value="analysis">
            <AnalysisTab />
          </TabsContent>
          <TabsContent value="treatment-plans">
            <TreatmentPlanTab />
          </TabsContent>
          <TabsContent value="consumables">
            <ConsumablesTab />
          </TabsContent>
          <TabsContent value="primary-specializations">
            <PrimarySpecializationsTab />
          </TabsContent>
        </TabsRoot>
      </WithSettingsNavbar>
    </WithSidebar>
  );
};

export const ResourceManagement = ResourceManagementContent;
